/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/** The facet group a drill-down is currently showing, or `null` for the list. */
export interface DrilldownGroup {
    attributeId: string;
    /** The group's own heading text, verbatim — the pane titles the second screen with it. */
    label: string;
}

export interface FacetDrilldown {
    group: DrilldownGroup | null;
    /**
     * `viaKeyboard` decides whether the pane MOVES FOCUS to its back control.
     *
     * Drilling in unmounts every focusable element in the pane, so a keyboard
     * user must be given a new landing point or they are stranded. A POINTER
     * user must not be: `.focus()` on a freshly mounted element makes Chromium
     * treat it as focus-visible, so the back control painted a focus ring after
     * a plain tap — measured on the running app, ring visible in
     * `.reference/live-bg-filter-drilldown/ours-BG-search-L2-390.png` before this
     * distinction existed.
     *
     * The discriminator is `MouseEvent.detail === 0`, which is what a click
     * synthesised from Enter or Space reports and what a real pointer click never
     * does. Cheaper and more reliable than tracking a global input modality, and
     * it is read at the only place that knows: the row's own handler.
     */
    open: (group: DrilldownGroup, options?: { viaKeyboard?: boolean }) => void;
    /** True when the current screen was reached from the keyboard. */
    openedViaKeyboard: boolean;
    back: () => void;
    /**
     * Return to the list and forget everything — what the PANE calls when it
     * closes, by any route.
     *
     * IT IS NOT THE SAME AS `back()`, and the difference is the reason this
     * exists: `back()` records `returningFrom` so the list can restore focus to
     * the row the shopper left. On a close there is no row to return to and the
     * pane's own trigger gets focus instead, so leaving that field set would
     * steal focus from the trigger on the next open.
     *
     * IT IS ALSO NOT UNMOUNTING, WHICH IS WHAT THIS USED TO RELY ON. The
     * provider sits ABOVE the `Sheet`, so Radix unmounting the pane's content
     * does not unmount the state — and the earlier comment here claiming it did
     * was wrong. Measured on the running app: closing from a value screen with
     * Escape and reopening landed on that same value screen, with the header
     * still reading `DESIGNER` and no list in the DOM. Live reopens on the list.
     */
    reset: () => void;
    /**
     * The group the LAST `back()` came out of, so the list can return focus to
     * the row the shopper left from rather than to the top of the pane. Cleared
     * by the list once it has been consumed.
     */
    returningFrom: string | null;
    clearReturningFrom: () => void;
}

/**
 * TWO-LEVEL FACET NAVIGATION, PROVIDED BY THE PANE AND CONSUMED BY THE RAIL.
 *
 * WHY A CONTEXT RATHER THAN PROPS. The two halves of a drill-down live in
 * different components and are composed by a THIRD:
 *
 *   the pane   `bg-mobile-filter-overlay` draws the header — the close/back
 *              control and the centred title — and swaps both when a group is
 *              open. It does not render the groups.
 *   the rail   `CategoryRefinements` draws the group rows and the values, and
 *              knows what the groups ARE. It does not render the header.
 *   the route  `_app.search` / `_app.category.$categoryId` composes them as
 *              `<BgMobileFilterOverlay><CategoryRefinements /></…>`.
 *
 * So the state has to be readable by a `children` subtree the pane does not
 * construct. Prop-drilling it would mean changing both route modules, and route
 * files on this repo are loader shells that no rail concern belongs in.
 *
 * IT IS ABSENT BY DEFAULT, AND THAT IS THE GATE. `useFacetDrilldown()` returns
 * `null` wherever no provider is mounted — both desktop rails, and NM's mobile
 * pane — and `CategoryRefinements` then takes byte-for-byte the code path it
 * took before this existed. The drill-down is therefore inert by construction
 * rather than by a brand check read at the bottom of a component: nothing in
 * either file names a brand or a width.
 *
 * BG-ONLY IS A PROPERTY OF THE MOUNT. `bg-mobile-filter-overlay` is the only
 * provider, and the routes mount it behind `ux.mobileFilterSortBar`, which is
 * BG's own composition-map flag. There is no `brand === 'bg'` anywhere in this
 * feature and no `window.innerWidth` branch — the pane itself is `md:hidden`,
 * a Tailwind responsive class, so SSR and hydration render the same tree.
 */
const FacetDrilldownContext = createContext<FacetDrilldown | null>(null);

/**
 * WHAT IS TRUE OF A RAIL BECAUSE IT IS INSIDE A MOBILE FILTER TAKEOVER, and not
 * because of which brand it is.
 *
 * Both panes mount this; neither desktop rail does. So a value here is read as
 * "the pane differs from the rail", which is the shape the evidence keeps
 * arriving in — twice in one afternoon, on two unrelated properties, with live
 * measurements on BOTH sides of each:
 *
 *   DEFAULT OPEN STATE   live's DESKTOP rails open Category (BG's 1728 capture
 *                        shows a minus on Category and a plus on the other
 *                        seven; live NM's served desktop markup expands Category
 *                        and Get it Fast). Live's MOBILE panes open NOTHING —
 *                        the owner, 2026-08-23, on both brands: *"live opens
 *                        with no filter options open, we have categories open.
 *                        same on bg actually."*
 *   CATEGORY CHILD INDENT
 *                        live's DESKTOP rail indents the level-1 rows 10.5 CSS
 *                        past the group header; its MOBILE pane sets them FLUSH.
 *                        Both ink-scanned, and the finding is a DELTA WITHIN one
 *                        image, so neither capture's scale enters it. See the
 *                        `--facet-cat-*` overrides in `overrides/search.css`.
 *
 * `useFacetMobilePane()` returns `null` on both desktop rails, so every rail
 * outside a pane keeps the code path it had — the same gating discipline the
 * drilldown context uses, and for the same reason.
 */
export interface FacetMobilePane {
    /**
     * Open no group on first paint. Deliberately narrower than "never open
     * anything": a group that carries an ACTIVE refinement still opens, because
     * the owner's report was about a pane with nothing applied and live's
     * behaviour in the refined state is unmeasured. Widening this needs a
     * capture of live's pane with a filter already on.
     */
    collapseAllByDefault: boolean;
}

const FacetMobilePaneContext = createContext<FacetMobilePane | null>(null);

const MOBILE_PANE: FacetMobilePane = { collapseAllByDefault: true };

/** Mounted by both mobile filter panes. See `FacetMobilePane`. */
export function FacetMobilePaneProvider({ children }: { children: ReactNode }) {
    return <FacetMobilePaneContext.Provider value={MOBILE_PANE}>{children}</FacetMobilePaneContext.Provider>;
}

/**
 * Pane-scoped facet behaviour, or `null` when this rail is not inside a pane.
 */
// A hook beside its provider costs this file fast refresh, which is the same
// trade `src/providers/wishlist.tsx` makes four times over. Splitting the two
// apart would put a context in one file and its only consumer in another for no
// gain at runtime.
// eslint-disable-next-line react-refresh/only-export-components
export function useFacetMobilePane(): FacetMobilePane | null {
    return useContext(FacetMobilePaneContext);
}

/**
 * Owns the drill state for one pane.
 *
 * REOPENING LANDS ON THE LIST, AND THAT IS THE PANE CALLING `reset()` RATHER
 * THAN THIS UNMOUNTING. The provider sits above the `Sheet` — it has to, because
 * the pane's own header reads the state to decide between a close mark and a
 * back mark — so Radix unmounting the pane's content leaves this component
 * mounted and its state intact. An earlier revision of this comment asserted the
 * opposite and was refuted on the running app: closing from a value screen with
 * Escape and reopening came back to that value screen, header still reading
 * `DESIGNER`. See `reset` on `FacetDrilldown`.
 */
export function FacetDrilldownProvider({ children }: { children: ReactNode }) {
    const [group, setGroup] = useState<DrilldownGroup | null>(null);
    const [returningFrom, setReturningFrom] = useState<string | null>(null);
    const [openedViaKeyboard, setOpenedViaKeyboard] = useState(false);

    const value = useMemo<FacetDrilldown>(
        () => ({
            group,
            openedViaKeyboard,
            open: (next, options) => {
                setReturningFrom(null);
                setOpenedViaKeyboard(Boolean(options?.viaKeyboard));
                setGroup(next);
            },
            /*
             * Read from the memo's own closure rather than from a `setGroup`
             * updater. Calling one setter from inside another's updater makes
             * the update impure — React may run an updater twice in StrictMode
             * and in concurrent rendering — and `value` already re-memoises on
             * every `group` change, so the closure is never stale.
             */
            back: () => {
                setReturningFrom(group?.attributeId ?? null);
                setGroup(null);
            },
            reset: () => {
                setGroup(null);
                setReturningFrom(null);
                setOpenedViaKeyboard(false);
            },
            returningFrom,
            clearReturningFrom: () => setReturningFrom(null),
        }),
        [group, returningFrom, openedViaKeyboard]
    );

    return <FacetDrilldownContext.Provider value={value}>{children}</FacetDrilldownContext.Provider>;
}

/**
 * The pane's drill state, or `null` when this rail is not inside a drill-down
 * pane. A `null` return is the normal case and is not an error — see the note
 * on `FacetDrilldownContext`.
 */
// Same trade as `useFacetMobilePane` above — see the note there.
// eslint-disable-next-line react-refresh/only-export-components
export function useFacetDrilldown(): FacetDrilldown | null {
    return useContext(FacetDrilldownContext);
}
